/**
 * 路由存取控制 — ROUTE_ACCESS_MAP 是唯一的 source of truth。
 *
 * status 說明：
 *   'wip'        開發中；Sidebar / Router 僅開發站放行，Permission Tree 永不顯示。
 *                整合測試完成後改為 'permission'。
 *   'permission' 需權限管控；path 與 permission 皆必填。
 *                Sidebar / Router 依 *.Read / *.Write 決定；Permission Tree 顯示。
 *
 * 不在 map 的路由：Sidebar 不顯示，Router 放行，Permission Tree 不顯示。
 *
 * 子路由繼承：以最長前綴比對（/o2o-management/scan-order 自動涵蓋其子路由）。
 * 環境判定：development / develop / dev_* → 開發站；其餘為正式環境。
 */

type WipEntry = {
  status: 'wip';
  path?: string;
  permission?: string;
};

type PermissionEntry = {
  status: 'permission';
  path: string;
  permission: string;
};

type RouteEntry = WipEntry | PermissionEntry;

export const ROUTE_ACCESS_MAP: readonly RouteEntry[] = [
  // 父路由 redirect 至 basic-setting，permission 比照 redirect 目標頁
  {
    path: '/o2o-management/scan-order',
    permission: 'O2O.PageScanOrderBasic',
    status: 'permission',
  },
  {
    path: '/o2o-management/scan-order/basic-setting',
    permission: 'O2O.PageScanOrderBasic',
    status: 'permission',
  },
  {
    path: '/o2o-management/scan-order/dine-in',
    permission: 'O2O.PageScanOrderDiningIn',
    status: 'permission',
  },
  {
    path: '/o2o-management/scan-order/take-out',
    permission: 'O2O.PageScanOrderDiningOut',
    status: 'permission',
  },

  {
    path: '/device-management/customer-display',
    permission: 'Device.PageCustomerDisplay',
    status: 'wip',
  },

  {
    path: '/o2o-management/multi-shop',
    permission: 'O2O.PageMultiShop',
    status: 'permission',
  },

  {
    path: '/online-order-reports/overview',
    permission: 'OnlineOrder.PageOnlineOrderOverview',
    status: 'permission',
  },

  {
    path: '/operation-reports/performance',
    permission: 'Insight.PagePerformance',
    status: 'permission',
  },
  {
    path: '/operation-reports/item-analysis',
    permission: 'Insight.PageItem',
    status: 'permission',
  },
  {
    path: '/operation-reports/label-analysis',
    permission: 'Insight.PageLabel',
    status: 'permission',
  },
  {
    path: '/operation-reports/discount-analysis',
    permission: 'Insight.PageDiscount',
    status: 'permission',
  },
  {
    path: '/operation-reports/closing-summary',
    permission: 'Insight.PageEodClose',
    status: 'permission',
  },
  {
    path: '/operation-reports/cash-drawer-management',
    permission: 'Insight.PageCashDrawer',
    status: 'permission',
  },

  {
    path: '/sales-reports/order',
    permission: 'Sales.PageOrder',
    status: 'permission',
  },
  {
    path: '/sales-reports/transaction',
    permission: 'Sales.PageTransaction',
    status: 'permission',
  },
  {
    path: '/sales-reports/invoice',
    permission: 'Sales.PageInvoice',
    status: 'permission',
  },

  {
    path: '/online-order-reports/record',
    permission: 'OnlineOrder.PageOnlineOrderReport',
    status: 'permission',
  },
  {
    path: '/product-management/items',
    permission: 'Product.PageItem',
    status: 'permission',
  },
  {
    path: '/product-management/container-weight',
    permission: 'Product.PageContainer',
    status: 'wip',
    // <!-- 與秤重相關，因為沒有要進test分支 -->
  },
  {
    path: '/product-management/categories',
    permission: 'Product.PageCategory',
    status: 'permission',
  },
  {
    path: '/product-management/bundles',
    permission: 'Product.PageBundle',
    status: 'permission',
  },
  {
    path: '/product-management/labels',
    permission: 'Product.PageLabel',
    status: 'permission',
  },
  {
    path: '/product-management/label-groups',
    permission: 'Product.PageLabelGroup',
    status: 'permission',
  },

  //M12-3 【產品_店家後台】配件管理
  {
    path: '/product-management/service-ware',
    permission: 'Product.PageServiceware',
    status: 'wip',
  },

  {
    path: '/discount',
    permission: 'Discount.PageDiscount',
    status: 'permission',
  },

  {
    path: '/order-settings/menu',
    permission: 'OrderSetting.PageMenu',
    status: 'permission',
  },
  {
    path: '/order-settings/keypad',
    permission: 'OrderSetting.PageKeyboard',
    status: 'permission',
  },

  {
    path: '/table-management',
    permission: 'Table.PageTable',
    status: 'permission',
  },

  { path: '/shop', permission: 'Shop.PageShop', status: 'permission' },

  {
    path: '/device-management/overview',
    permission: 'Device.PageOverview',
    status: 'permission',
  },
  {
    path: '/device-management/pos',
    permission: 'Device.PagePOS',
    status: 'permission',
  },
  {
    path: '/device-management/pos-setting',
    permission: 'Device.PagePOSSettings',
    status: 'permission',
  },
  {
    path: '/device-management/printer',
    permission: 'Device.PagePrinter',
    status: 'permission',
  },

  {
    path: '/customer-management/members',
    permission: 'ConsumerMember.PageMember',
    status: 'permission',
  },
  {
    path: '/customer-management/frequent-contacts',
    permission: 'ConsumerMember.PageConsumer',
    status: 'permission',
  },
  {
    path: '/customer-management/member-system',
    permission: 'ConsumerMember.PageMemberSetting',
    status: 'permission',
  },
  {
    path: '/customer-management/member-labels',
    permission: 'ConsumerMember.PageMemberTag',
    status: 'permission',
  },

  {
    path: '/staff-management/staff',
    permission: 'Employee.PageEmployee',
    status: 'permission',
  },
  {
    path: '/staff-management/roles',
    permission: 'Employee.PageRole',
    status: 'permission',
  },
  {
    path: '/staff-management/authorization',
    permission: 'Employee.PagePermissionVerify',
    status: 'permission',
  },

  {
    path: '/o2o-management/online-order/basic-setting',
    permission: 'O2O.PageO2OBasic',
    status: 'permission',
  },
  {
    path: '/o2o-management/online-order/meal-take-method',
    permission: 'O2O.PageO2ODiningOption',
    status: 'permission',
  },
  {
    path: '/o2o-management/online-order/ordering-time',
    permission: 'O2O.PageO2OOrderingTime',
    status: 'permission',
  },
  {
    path: '/o2o-management/multi-shop/same-brand',
    permission: 'O2O.PageMultiShop',
    status: 'permission',
  },
  {
    path: '/o2o-management/multi-shop/cross-brand',
    permission: 'O2O.PageCrossBrandMultiShop',
    status: 'permission',
  },

  {
    path: '/application-management',
    permission: 'App.PageApp',
    status: 'permission',
  },

  {
    path: '/system-publish',
    permission: 'SystemPublish.PageSystemPublish',
    status: 'permission',
  },

  {
    path: '/system-settings/headquarter',
    permission: 'SystemSetting.PageWorkspace',
    status: 'permission',
  },
  {
    path: '/system-settings/brand',
    permission: 'SystemSetting.PageBrand',
    status: 'permission',
  },
  {
    path: '/system-settings/taxes',
    permission: 'SystemSetting.PageTax',
    status: 'permission',
  },
  {
    path: '/system-settings/dining-option',
    permission: 'SystemSetting.PageDiningOption',
    status: 'permission',
  },
  {
    path: '/system-settings/payment-methods',
    permission: 'SystemSetting.PageCheckoutPaymentMethod',
    status: 'permission',
  },
  {
    path: '/system-settings/minimum-charge',
    permission: 'SystemSetting.PageCheckoutMinimumCharge',
    status: 'wip',
  },
  {
    path: '/system-settings/service-fee',
    permission: 'SystemSetting.PageCheckoutServiceFee',
    status: 'permission',
  },
  {
    path: '/system-settings/discount-rule',
    permission: 'SystemSetting.PageCheckoutDiscountRule',
    status: 'permission',
  },
  {
    path: '/system-settings/invoice-setting',
    permission: 'SystemSetting.PageCheckoutInvoice',
    status: 'permission',
  },
  {
    path: '/system-settings/reason/manual-discount',
    permission: 'SystemSetting.PageReasonManualDiscount',
    status: 'permission',
  },
  {
    path: '/system-settings/reason/refund-void',
    permission: 'SystemSetting.PageReasonVoidRefund',
    status: 'permission',
  },
  {
    path: '/system-settings/reason/open-cash-drawer',
    permission: 'SystemSetting.PageReasonOpenCashDrawer',
    status: 'permission',
  },
  {
    path: '/system-settings/reason/return-item',
    permission: 'SystemSetting.PageReasonReturnItem',
    status: 'permission',
  },
  {
    path: '/system-settings/reason/complimentary',
    permission: 'SystemSetting.PageReasonComplimentary',
    status: 'permission',
  },
  {
    path: '/system-settings/reason/o2o-refund',
    permission: 'SystemSetting.PageReasonO2OReject',
    status: 'permission',
  },
  {
    path: '/system-settings/reason/o2o-cancel',
    permission: 'SystemSetting.PageReasonO2ODelete',
    status: 'permission',
  },
  {
    path: '/system-settings/order-remarks',
    permission: 'SystemSetting.PageOrderRemark',
    status: 'wip',
  },
  {
    path: '/system-settings/pos-update',
    permission: 'SystemSetting.PagePOSUpdate',
    status: 'wip',
  },
  {
    path: '/ai-waiter/basic-setting',
    permission: 'AIWaiter.PageBasicSetting',
    status: 'wip',
  },
  {
    path: '/ai-waiter/virtual-human',
    permission: 'AIWaiter.PageVirtualHuman',
    status: 'wip',
  },
  // TODO: SystemSetting.PageCallMealNote  — 路由尚未建立
  // TODO: SystemSetting.PageConsumerType — 路由尚未建立
];

// build-time 常數，打包後不變
const _mode = import.meta.env.MODE;
export const IS_DEV_ENV: boolean =
  _mode === 'development' ||
  _mode === 'develop' ||
  _mode.startsWith('dev_') ||
  _mode.startsWith('develop_');

// 完全比對優先，其次取最長前綴（處理 /path/edit/123 等子路由）
function resolveEntry(path: string): RouteEntry | undefined {
  const exact = ROUTE_ACCESS_MAP.find((e) => e.path === path);
  if (exact) return exact;

  let bestMatch: { pathLength: number; entry: RouteEntry } | undefined;
  for (const entry of ROUTE_ACCESS_MAP) {
    if (!entry.path) continue;
    if (path.startsWith(entry.path + '/')) {
      if (!bestMatch || entry.path.length > bestMatch.pathLength) {
        bestMatch = { pathLength: entry.path.length, entry };
      }
    }
  }
  return bestMatch?.entry;
}

export function isWipRoute(path: string): boolean {
  return resolveEntry(path)?.status === 'wip';
}

/** wip 路由在非開發站是否應 redirect */
export function isWipBlocked(path: string): boolean {
  return isWipRoute(path) && !IS_DEV_ENV;
}

/** 取得路徑對應的 permission prefix（status: 'permission' 才有值） */
export function getPermissionForPath(path: string): string | undefined {
  const entry = resolveEntry(path);
  if (entry?.status === 'permission') return entry.permission;
  return undefined;
}

/** Permission Tree 白名單：只顯示有對應前端路由的 permission */
export const PERMITTED_PREFIXES: readonly string[] = ROUTE_ACCESS_MAP.filter(
  (e): e is PermissionEntry => e.status === 'permission',
).map((e) => e.permission);

const POS_PERMISSION_PREFIXES: readonly string[] = [
  'Login',
  'Ordering',
  'Discount',
  'Billing',
  'EodShift',
  'OnlineOrder',
  'DDMember',
];

/** Role/Staff permission selector 白名單：包含後台 route permissions 與 POS 操作權限。 */
export const ROLE_PERMISSION_SELECTOR_PREFIXES: readonly string[] = [
  ...PERMITTED_PREFIXES,
  ...POS_PERMISSION_PREFIXES,
];
